import React from 'react';

import { ROTATION_STATUSES } from '@/config/constants';
import {
	AnalysisOutputItem,
	FullAnalysisOutput,
	ListSongsQuery,
	ReanalyzeSongMutation,
	useListSongsQuery,
	useReanalyzeSongMutation,
	useUpdateSongMutation,
} from '@/graphql';
import {
	AspectRatio,
	Box,
	Button,
	Card,
	CardBody,
	CardFooter,
	CardProps,
	Center,
	Divider,
	Flex,
	Image,
	Popover,
	PopoverArrow,
	PopoverBody,
	PopoverContent,
	PopoverHeader,
	PopoverTrigger,
	Select,
	Tag,
	Text,
	Wrap,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

type SongRowProps = CardProps & {
	id: string;
	name: string;
	artworkUrl?: string | null;
	artist: string;
	footer?: React.ReactNode;
	songThemes?: {
		emotionTags: string[];
		energyTags: string[];
		gospelTags: string[];
		songTypeTags: string[];
	};
	overlay?: React.ReactNode;
	overlayColor?: string;
	variations?: string;
	analysisOutput?: FullAnalysisOutput | null;
	rotationStatus?: string | null;
	onRotationStatusChange?: () => void;
	onReanalyzeSuccess?: (data: ReanalyzeSongMutation) => void;
	teamCount?: number | null;
};

const SongRow: React.FC<SongRowProps> = ({
	id,
	name,
	artworkUrl,
	artist,
	footer,
	songThemes,
	overlay,
	overlayColor,
	variations,
	analysisOutput,
	rotationStatus,
	onRotationStatusChange,
	onReanalyzeSuccess,
	teamCount,
	...props
}) => {
	const queryClient = useQueryClient();
	const hasVariations = variations && variations.length > 2;
	const hasAnalysisOutput = !!analysisOutput;

	const {
		mutateAsync: analyzeMutateAsync,
		error: analyzeSongError,
		isLoading: analyzeSongLoading,
	} = useReanalyzeSongMutation({
		onSuccess: (data) => {
			onReanalyzeSuccess?.(data);
		},
	});

	const {
		mutateAsync: updateMutateAsync,
		error: updateSongError,
		isLoading: updateSongLoading,
	} = useUpdateSongMutation({
		onSuccess: onRotationStatusChange,
	});

	return (
		<Flex
			width="100%"
			gap={2}
			{...props}
			display="inline-flex"
			alignItems="center"
			border="1px solid #e2e8f0"
			p={2}
		>
			<Box position="relative" flexShrink={0}>
				<Image
					width="40px"
					height="40px"
					src={artworkUrl ?? ''}
					fallbackSrc="/album.png"
				/>
				{overlay ? (
					<AspectRatio
						marginTop="-100%"
						opacity={0.7}
						w="100%"
						background={overlayColor ? overlayColor : 'blue.200'}
						ratio={1}
					>
						<Center>
							<Text
								fontSize="30px"
								color="white"
								fontWeight="bold"
							>
								{overlay}
							</Text>
						</Center>
					</AspectRatio>
				) : null}
			</Box>
			<Box flex={1} minWidth="250px">
				<Text mt="10px" as="b">
					{name}
				</Text>
				<Text>{artist}</Text>
			</Box>
			{songThemes && (
				<Wrap mt={2}>
					{[
						...songThemes.energyTags,
						...songThemes.emotionTags,
						...songThemes.gospelTags,
						...songThemes.songTypeTags,
					].map((t) => (
						<Tag size="sm">{t}</Tag>
					))}
				</Wrap>
			)}
			<Button
				size="sm"
				width="90px"
				flexShrink={0}
				isDisabled
				colorScheme={hasVariations ? 'green' : 'gray'}
				variant={hasVariations ? 'solid' : 'outline'}
			>
				Variations
			</Button>
			<Popover trigger="hover" placement="bottom-start" isLazy>
				<PopoverTrigger>
					<Button
						size="sm"
						width="90px"
						flexShrink={0}
						colorScheme={hasAnalysisOutput ? 'green' : 'gray'}
						variant={hasAnalysisOutput ? 'solid' : 'outline'}
						isLoading={analyzeSongLoading}
						onClick={() => {
							analyzeMutateAsync({ songId: id });
						}}
					>
						Analysis
					</Button>
				</PopoverTrigger>
				<PopoverContent width="180px">
					<PopoverArrow />
					<PopoverHeader fontSize="xs" fontWeight="semibold" pb={1}>
						Breakdown
					</PopoverHeader>
					<PopoverBody px={3} py={2}>
						<Text fontSize="xs">
							Emotions: {analysisOutput?.emotions?.length ?? '–'}
						</Text>
						<Text fontSize="xs">
							Themes:{' '}
							{analysisOutput?.thematicKeywords?.length ?? '–'}
						</Text>
						<Text fontSize="xs">
							Theology:{' '}
							{analysisOutput?.theologicalKeywords?.length ?? '–'}
						</Text>
						<Text fontSize="xs">
							Gospel Story:{' '}
							{analysisOutput?.gospelStory?.length ?? '–'}
						</Text>
						<Text fontSize="xs">
							Song type: {songThemes?.songTypeTags?.[0] ?? '–'}
						</Text>
					</PopoverBody>
				</PopoverContent>
			</Popover>
			{teamCount !== undefined && (
				<Tag
					size="sm"
					colorScheme="purple"
					flexShrink={0}
					minWidth="60px"
					justifyContent="center"
				>
					{teamCount} teams
				</Tag>
			)}
			<Select
				width="120px"
				flexShrink={0}
				size="sm"
				name="rotationStatus"
				onChange={(e) => {
					updateMutateAsync({
						songId: id,
						input: {
							artist,
							name,
							rotationStatus: e.currentTarget.value,
						},
					});
					console.log(e.currentTarget.value);
				}}
				placeholder="None"
			>
				{ROTATION_STATUSES.map(({ key }) => (
					<option selected={rotationStatus === key} value={key}>
						{key}
					</option>
				))}
			</Select>
			{footer}
		</Flex>
	);
};

export default SongRow;
